<script lang="ts">
  import { Download, RefreshCw, X, AlertCircle, CheckCircle } from 'lucide-svelte'
  import { updateStore, updateActions, type UpdateState } from '$lib/stores/updateStore'
  import { demoTracker } from '$lib/stores/demoStore'
  import { marked } from 'marked'
  import DOMPurify from 'dompurify'

  let showNotification = $state(false)
  let currentState = $state<UpdateState | null>(null)
  let autoCloseTimeout: number | null = null

  // Auto-show notification when update states change
  $effect(() => {
    const state = $updateStore

    // Don't show update notifications in demo mode
    if (demoTracker.isDemoMode()) {
      showNotification = false
      return
    }

    // Show notification for these states
    if (state.updateAvailable || state.downloading || state.updateDownloaded || state.error) {
      showNotification = true
      currentState = state

      // Auto-close after 10 seconds for non-critical states
      if (autoCloseTimeout) {
        clearTimeout(autoCloseTimeout)
      }

      // Don't auto-close for updateDownloaded (needs user action)
      if (!state.updateDownloaded) {
        autoCloseTimeout = window.setTimeout(() => {
          showNotification = false
        }, 10000)
      }
    }
  })

  function closeNotification() {
    showNotification = false
    if (autoCloseTimeout) {
      clearTimeout(autoCloseTimeout)
      autoCloseTimeout = null
    }
  }

  function handleDownload() {
    updateActions.downloadUpdate()
    // Keep notification open to show progress
  }

  function handleInstall() {
    updateActions.installUpdate()
    closeNotification()
  }

  function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
  }

  // Render markdown safely
  function renderMarkdown(markdown: string): string {
    if (!markdown) return ''
    const html = marked.parse(markdown, {
      async: false,
      breaks: true,
      gfm: true
    }) as string
    return DOMPurify.sanitize(html)
  }
</script>

{#if showNotification && currentState}
  <div class="notification-container">
    <button class="close-btn" onclick={closeNotification} aria-label="Close">
      <X size={16} />
    </button>

    <!-- Update Available -->
    {#if currentState.updateAvailable && !currentState.downloading && !currentState.updateDownloaded}
      <div class="notification-content">
        <div class="notification-header">
          <Download size={18} class="icon" />
          <span class="notification-title">새 버전 사용 가능</span>
        </div>

        {#if currentState.updateInfo}
          <div class="notification-body">
            <span class="version">v{currentState.updateInfo.version}</span>
          </div>
        {/if}

        <div class="notification-actions">
          <button class="action-btn secondary" onclick={closeNotification}>나중에</button>
          <button class="action-btn primary" onclick={handleDownload}>
            <Download size={14} />
            다운로드
          </button>
        </div>
      </div>
    {/if}

    <!-- Downloading -->
    {#if currentState.downloading && currentState.downloadProgress}
      <div class="notification-content">
        <div class="notification-header">
          <Download size={18} class="icon downloading" />
          <span class="notification-title">다운로드 중</span>
        </div>

        <div class="notification-body">
          <div class="progress-bar">
            <div class="progress-fill" style="width: {currentState.downloadProgress.percent}%"></div>
          </div>
          <div class="progress-info">
            <span class="progress-percent">{currentState.downloadProgress.percent.toFixed(0)}%</span>
            <span class="progress-speed">{formatBytes(currentState.downloadProgress.bytesPerSecond)}/s</span>
          </div>
        </div>
      </div>
    {/if}

    <!-- Download Complete -->
    {#if currentState.updateDownloaded}
      <div class="notification-content">
        <div class="notification-header">
          <CheckCircle size={18} class="icon success" />
          <span class="notification-title">업데이트 준비 완료</span>
        </div>

        {#if currentState.updateInfo}
          <div class="notification-body">
            <span class="version">v{currentState.updateInfo.version}</span>
            <p class="message">재시작하여 업데이트를 설치하세요</p>
          </div>
        {/if}

        <div class="notification-actions">
          <button class="action-btn secondary" onclick={closeNotification}>나중에</button>
          <button class="action-btn primary" onclick={handleInstall}>
            <RefreshCw size={14} />
            재시작
          </button>
        </div>
      </div>
    {/if}

    <!-- Error -->
    {#if currentState.error && !currentState.updateAvailable}
      <div class="notification-content">
        <div class="notification-header">
          <AlertCircle size={18} class="icon error" />
          <span class="notification-title">업데이트 실패</span>
        </div>

        <div class="notification-body">
          <p class="error-message">{currentState.error}</p>
        </div>

        <div class="notification-actions">
          <button class="action-btn primary" onclick={closeNotification}>확인</button>
        </div>
      </div>
    {/if}
  </div>
{/if}

<style>
  .notification-container {
    position: fixed;
    top: 52px; /* TabBar height (36px) + spacing (16px) */
    right: 16px;
    width: 320px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    backdrop-filter: blur(8px);
    z-index: 9999;
    animation: slideInRight 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .notification-container:hover {
    transform: translateX(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .close-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 4px;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.6;
    transition: opacity 0.15s, background-color 0.15s;
  }

  .close-btn:hover {
    opacity: 1;
    background: var(--bg-tertiary);
  }

  .notification-content {
    padding: 12px 14px;
  }

  .notification-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  .notification-header :global(.icon) {
    color: var(--accent-primary);
    flex-shrink: 0;
  }

  .notification-header :global(.icon.success) {
    color: var(--success);
  }

  .notification-header :global(.icon.error) {
    color: var(--error);
  }

  .notification-title {
    font-size: var(--ui-font-size-md);
    font-weight: 500;
    color: var(--text-primary);
  }

  .notification-body {
    margin-bottom: 10px;
    padding-left: 26px;
  }

  .version {
    font-size: var(--ui-font-size-sm);
    color: var(--text-primary);
    font-weight: 500;
    background: var(--bg-tertiary);
    padding: 2px 8px;
    border-radius: 4px;
    display: inline-block;
  }

  .message {
    font-size: var(--ui-font-size-sm);
    color: var(--text-secondary);
    margin: 4px 0 0 0;
  }

  .error-message {
    font-size: var(--ui-font-size-sm);
    color: var(--error);
    margin: 0;
    line-height: 1.4;
  }

  .progress-bar {
    width: 100%;
    height: 3px;
    background: var(--bg-tertiary);
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 6px;
  }

  .progress-fill {
    height: 100%;
    background: var(--accent-primary);
    transition: width 0.3s ease;
    position: relative;
    overflow: hidden;
  }

  .progress-fill::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  .progress-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: var(--ui-font-size-xs);
  }

  .progress-percent {
    color: var(--text-primary);
    font-weight: 500;
  }

  .progress-speed {
    color: var(--text-secondary);
  }

  .notification-actions {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
    padding-left: 26px;
  }

  .action-btn {
    padding: 6px 12px;
    border-radius: 4px;
    font-size: var(--ui-font-size-sm);
    font-weight: 500;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: all 0.15s;
  }

  .action-btn.primary {
    background: var(--btn-primary-bg);
    color: var(--btn-primary-text);
  }

  .action-btn.primary:hover {
    background: var(--btn-primary-hover);
  }

  .action-btn.secondary {
    background: var(--btn-secondary-bg);
    color: var(--btn-secondary-text);
    border: 1px solid var(--btn-secondary-border);
  }

  .action-btn.secondary:hover {
    background: var(--btn-secondary-hover);
  }

  .action-btn:active {
    transform: scale(0.98);
  }

  /* Dark theme adjustments */
  @media (prefers-color-scheme: dark) {
    .notification-container {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    .notification-container:hover {
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
    }
  }

  :global(.dark-theme) .notification-container {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  :global(.dark-theme) .notification-container:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
  }
</style>
